<template>
  <filter-panel
    class="filter-panel"
    :apply-filter-immediately="false"
    :filter-parameters="formattedFilters"
    @update="applyFilter"
    :notification-text="null"
    :api-request-data="apiRequestData">
  </filter-panel>
</template>


<script>
import getNested from 'lodash-es/get';
import { mapActions, mapGetters } from 'vuex';
import FilterPanel from '../widgets/FilterPanel';

import {
  getAPIFilterValues,
  applyFilterValues,
  getCurrentFilterValues,
} from '@/components/widgets/FilterPanel';
import isEmpty from 'lodash-es/isEmpty';
import cloneDeep from 'lodash-es/cloneDeep';


export default {
  name: 'filters',
  components: {
    FilterPanel,
  },

  props: {
    apiRequestData: {
      type: Object,
      default: () => ({
        voyages: {},
        voyagesPeriodicStat: {},
      })
    },
  },

  data() {
    return {
      filtersReady: false,
      defaultFilterValues: null,

      filterSchema: {
        segment: {
          name: 'segment',
          apiName: 'segment',
          type: 'multiselect',
          placeholder: 'Segment',
          value: [],
          choices: [
            {'text': 'Capesize', 'value': 'capesize'},
            {'text': 'Handysize', 'value': 'handysize'},
            {'text': 'Panamax', 'value': 'panamax'},
            {'text': 'Supramax', 'value': 'supramax'},
          ],
          defaultChoice: null,
        },

        ladenStatus: {
          name: 'ladenStatus',
          apiName: 'ladenStatus',
          type: 'multiselect',
          placeholder: 'Laden status',

          value: [],
          choices: [
            {text: 'Laden', value: 'Laden'},
            {text: 'Ballast', value: 'Ballast'},
          ],
          defaultChoice: [],
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        loadCountry: {
          name: 'loadCountry',
          apiName: 'loadCountry',
          type: 'multiselect',
          placeholder: 'Load country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },

        dischargeCountry: {
          name: 'dischargeCountry',
          apiName: 'dischargeCountry',
          type: 'multiselect',
          placeholder: 'Discharge country',
          value: [],
          choices: [],
          defaultChoice: null,
          options: {
            toggleAll: true,
          },
        },
      },  // <-- end of filterSchema
    };
  },

  methods: {
    ...mapActions(
      'api/tradeflows', [
        'getCommodities',
        'getCountries',
        'getRegions',
        'getTradeflowsRegionsWithPolygons',
      ]
    ),

    async loadFilters() {
      if(this.filtersReady) {
        return;
      }

      await Promise.all([
        this.loadCommodityChoices(),
        this.loadRegionChoices([]),
      ]);

      this.filtersReady = true;
    },

    async loadCommodityChoices() {
      this.filterSchema.commodity.choices = await this.getCommodities({group: true});
    },

    async loadRegionChoices() {
      await Promise.all([
        this.loadChoices('loadCountry', 'getCountries', {direction: 'export'}),
        this.loadChoices('dischargeCountry', 'getCountries', {direction: 'import'}),
      ]);
    },

    async loadChoices(fieldName, apiMethod, apiParameters) {
      this.filterSchema[fieldName].choices = await this[apiMethod](apiParameters);
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        // to prevent a few submissions when filters are set initially
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);
      let apiFilterValues = getAPIFilterValues(this.filterSchema, filterValues);
      this.$emit('search', apiFilterValues);
    },

    resetSelectToDefaultIfRequired(field) {
      // if commidity is changed, new origin/destination countries list
      // can have choices without a currently selected value
      // this case it's required to reset select to default value and apply filters again
      // field is a "this.originCountry" or "this.destinationCountry" for example

      const choicesValues = field.choices.map(choice => choice.value);
      let existingValues = field.value.filter(item => choicesValues.includes(item.value));

      if(!existingValues.length) {
        if(field.defaultChoice) {
          field.value = field.defaultChoice.value;
        } else {
          field.value = [];
        }
      }
    },

    async applyFilterPreset() {
      const filterValues = getNested(this.getActivePreset, 'params.filterPanel', {});
      applyFilterValues(this.filterSchema, filterValues);
    },

    resetFilters() {
      // console.log('reset filterValues', this.defaultFilterValues);
      applyFilterValues(this.filterSchema, cloneDeep(this.defaultFilterValues));
    },

    saveDefaultValues() {
      if(!isEmpty(this.defaultFilterValues)) {
        return;
      }

      this.defaultFilterValues = getCurrentFilterValues(this.filterSchema);
      // special case for the commodity selector
      const selectedItem = this.filterSchema.commodity.choices.find(item => item.id === 'Aluminum');
      if(selectedItem) {
        // this.filterSchema.commodity.value = selectedItem.children;
        this.defaultFilterValues.commodity = selectedItem.children;
      }
    },

    setDefaultValues() {
      applyFilterValues(this.filterSchema, this.defaultFilterValues);
    },
  },

  computed: {
    ...mapGetters('api/presets', ['getActivePreset']),

    formattedFilters() {
      // we can't use common way with getFormattedFilters(), since 3 rows are required
      return {
        groups: [
          [
            // this.filterSchema.showSubCommodities,
            this.filterSchema.segment,
            this.filterSchema.commodity,
          ],
          [
            this.filterSchema.loadCountry,
            this.filterSchema.dischargeCountry,
            this.filterSchema.ladenStatus,
          ]
        ]
      };
    },
  },

  watch: {
    'filterSchema.loadCountry.choices': function () {
      this.resetSelectToDefaultIfRequired(this.filterSchema.loadCountry);
    },

    'filterSchema.dischargeCountry.choices': function () {
      this.resetSelectToDefaultIfRequired(this.filterSchema.dischargeCountry);
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilters();
          this.saveDefaultValues();

          if(!preset) {
            this.setDefaultValues();
          }
        }

        if(preset) {
          await this.applyFilterPreset(preset);
        }
      },
    }
  },
}
</script>

<style scoped>
.filter-panel >>> .multiselect__tags {
  min-width: 200px;
}

.filter-panel >>> .custom-select {
  min-width: 150px;
}
</style>

<style scoped lang="scss">
.filter-panel {
  margin-bottom: 1.5rem;
}
</style>
